import { ImagePlus, Trash2 } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";

import {
  attachArtwork,
  deleteTrack,
  patchTrack,
  type TrackPatchInput,
} from "../../api/library.js";
import { Button } from "../../design-system/button.js";
import { Field, NumberInput, TextInput } from "../../design-system/form.js";
import { InlineConfirm } from "../../design-system/inline-confirm.js";
import { Modal } from "../../design-system/modal.js";
import { Notice } from "../../design-system/surfaces.js";
import { Artwork } from "../../player/artwork.js";
import {
  formatBytes,
  formatCuePoint,
  formatDate,
  formatDuration,
} from "../../lib/format.js";
import type { Track } from "../../api/types.js";

/**
 * One track of the library, edited.
 *
 * Cue points are the track's own property and every feed that plays it plays it the
 * same way — that is primitive 1, and it is why they are edited here rather than
 * anywhere a feed is configured. The playable stretch is drawn against the decoded
 * duration so what is being trimmed is visible rather than arithmetic.
 */
export function TrackEditor({
  onChanged,
  onClose,
  onDeleted,
  track,
}: {
  onChanged: (track: Track) => void;
  onClose: () => void;
  onDeleted: (trackId: string) => void;
  track: Track | null;
}) {
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [album, setAlbum] = useState("");
  const [year, setYear] = useState("");
  const [genre, setGenre] = useState("");
  const [tags, setTags] = useState("");
  const [cueIn, setCueIn] = useState("0");
  const [cueOut, setCueOut] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [artworkBusy, setArtworkBusy] = useState(false);
  const artworkInput = useRef<HTMLInputElement | null>(null);

  /**
   * The form is loaded when a different track is opened, and at no other time. It
   * used to reload whenever the track object changed — which meant attaching artwork,
   * whose only job is to hand back a new media id, threw away every edit the operator
   * had typed and not yet saved.
   */
  const loaded = useRef<string | null>(null);
  useEffect(() => {
    if (!track) {
      loaded.current = null;
      return;
    }
    if (loaded.current === track.id) return;
    loaded.current = track.id;
    setTitle(track.title);
    setArtist(track.artist);
    setAlbum(track.album);
    setYear(track.year === null ? "" : String(track.year));
    setGenre(track.genre);
    setTags(track.tags.join(", "));
    setCueIn(String(track.cueInMs));
    setCueOut(track.cueOutMs === null ? "" : String(track.cueOutMs));
    setError(null);
    setSaved(false);
  }, [track]);

  if (!track) return null;

  const duration = track.durationMs;
  const cueInMs = Number(cueIn);
  const cueOutMs = cueOut.trim() === "" ? null : Number(cueOut);
  const cueValid =
    Number.isFinite(cueInMs) &&
    cueInMs >= 0 &&
    cueInMs < duration &&
    (cueOutMs === null ||
      (Number.isFinite(cueOutMs) &&
        cueOutMs > cueInMs &&
        cueOutMs <= duration));
  const playableMs = (cueOutMs ?? duration) - cueInMs;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!cueValid || busy || !track) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    const input: TrackPatchInput = {
      title: title.trim() || track.title,
      artist: artist.trim(),
      album: album.trim(),
      genre: genre.trim(),
      year: year.trim() === "" ? null : Number(year),
      tags: tags
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag !== ""),
      cue_in_ms: Math.round(cueInMs),
      cue_out_ms: cueOutMs === null ? null : Math.round(cueOutMs),
    };
    patchTrack(track.id, input)
      .then((updated) => {
        if (updated) onChanged(updated);
        setSaved(true);
      })
      .catch((cause: unknown) => {
        setError(
          cause instanceof Error ? cause.message : "The track was not saved.",
        );
      })
      .finally(() => {
        setBusy(false);
      });
  }

  function chooseArtwork(file: File | undefined) {
    if (!file || !track) return;
    setArtworkBusy(true);
    setError(null);
    attachArtwork(track.id, file)
      .then((artworkMediaId) => {
        // The artwork is served from the track's own address, so the new media id is
        // what tells the browser the bytes behind that address are not the ones it
        // already has.
        onChanged({ ...track, artworkMediaId });
      })
      .catch((cause: unknown) => {
        setError(
          cause instanceof Error
            ? cause.message
            : "The artwork was not attached.",
        );
      })
      .finally(() => {
        setArtworkBusy(false);
      });
  }

  return (
    <Modal
      description="Changes apply everywhere this track plays."
      onClose={onClose}
      open={track !== null}
      title="Edit track"
      width="44rem"
    >
      <form className="flex flex-col gap-5" noValidate onSubmit={submit}>
        <div className="flex flex-wrap items-start gap-4">
          <Artwork
            className="size-24"
            src={
              track.artworkMediaId
                ? `/api/artwork/${encodeURIComponent(track.id)}?v=${encodeURIComponent(track.artworkMediaId)}`
                : null
            }
          />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <input
              accept="image/*"
              className="hidden"
              onChange={(event) => {
                chooseArtwork(event.target.files?.[0]);
                event.target.value = "";
              }}
              ref={artworkInput}
              type="file"
            />
            <Button
              disabled={artworkBusy}
              onClick={() => artworkInput.current?.click()}
              size="small"
              variant="secondary"
            >
              <ImagePlus aria-hidden="true" />
              {artworkBusy
                ? "Attaching…"
                : track.artworkMediaId
                  ? "Replace artwork"
                  : "Attach artwork"}
            </Button>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-[0.6875rem] text-ink-3">
              <div className="flex justify-between gap-2">
                <dt>Duration</dt>
                <dd className="text-ink-2">{formatDuration(duration)}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt>Format</dt>
                <dd className="text-ink-2">
                  {track.codec.toUpperCase()}
                  {track.bitrateKbps ? ` · ${String(track.bitrateKbps)}k` : ""}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt>Size</dt>
                <dd className="text-ink-2">{formatBytes(track.byteSize)}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt>Added</dt>
                <dd className="text-ink-2">{formatDate(track.createdAt)}</dd>
              </div>
            </dl>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field className="sm:col-span-2" htmlFor="track-title" label="Title">
            <TextInput
              autoFocus
              id="track-title"
              onChange={(event) => {
                setTitle(event.target.value);
              }}
              required
              value={title}
            />
          </Field>
          <Field htmlFor="track-artist" label="Artist">
            <TextInput
              id="track-artist"
              onChange={(event) => {
                setArtist(event.target.value);
              }}
              value={artist}
            />
          </Field>
          <Field htmlFor="track-album" label="Album">
            <TextInput
              id="track-album"
              onChange={(event) => {
                setAlbum(event.target.value);
              }}
              value={album}
            />
          </Field>
          <Field htmlFor="track-genre" label="Genre">
            <TextInput
              id="track-genre"
              onChange={(event) => {
                setGenre(event.target.value);
              }}
              value={genre}
            />
          </Field>
          <Field htmlFor="track-year" label="Year">
            <NumberInput
              id="track-year"
              max={9999}
              min={0}
              onChange={(event) => {
                setYear(event.target.value);
              }}
              value={year}
            />
          </Field>
          <Field
            className="sm:col-span-2"
            hint="Separate with commas."
            htmlFor="track-tags"
            label="Tags"
          >
            <TextInput
              id="track-tags"
              onChange={(event) => {
                setTags(event.target.value);
              }}
              value={tags}
            />
          </Field>
        </div>

        <section className="rounded-sm border border-border bg-bg-2 p-4">
          <h3 className="text-sm font-semibold text-ink">Cue points</h3>
          <p className="mt-1 text-xs leading-5 text-ink-3">
            Where this track starts and stops playing.
          </p>
          <CueBar cueInMs={cueInMs} cueOutMs={cueOutMs} durationMs={duration} />
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field
              hint={`Starts at ${formatCuePoint(cueInMs)}`}
              htmlFor="track-cue-in"
              label="Cue in"
            >
              <NumberInput
                id="track-cue-in"
                max={Math.max(0, duration - 1)}
                min={0}
                onChange={(event) => {
                  setCueIn(event.target.value);
                }}
                step={100}
                unit="ms"
                value={cueIn}
              />
            </Field>
            <Field
              hint={
                cueOutMs === null
                  ? "Empty plays to the end of the file."
                  : `Stops at ${formatCuePoint(cueOutMs)}`
              }
              htmlFor="track-cue-out"
              label="Cue out"
            >
              <NumberInput
                id="track-cue-out"
                max={duration}
                min={0}
                onChange={(event) => {
                  setCueOut(event.target.value);
                }}
                placeholder="End of file"
                step={100}
                unit="ms"
                value={cueOut}
              />
            </Field>
          </div>
          <p className="mt-3 font-mono text-[0.6875rem] text-ink-3">
            {cueValid
              ? `Plays ${formatDuration(playableMs)} of ${formatDuration(duration)}`
              : "The playable stretch must be inside the decoded duration."}
          </p>
        </section>

        {error ? <Notice tone="danger">{error}</Notice> : null}
        {saved && !error ? <Notice tone="neutral">Saved.</Notice> : null}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
          <InlineConfirm
            confirmLabel="Remove from library"
            onConfirm={() => {
              setBusy(true);
              deleteTrack(track.id)
                .then(() => {
                  onDeleted(track.id);
                  onClose();
                })
                .catch((cause: unknown) => {
                  setError(
                    cause instanceof Error
                      ? cause.message
                      : "The track was not removed.",
                  );
                })
                .finally(() => {
                  setBusy(false);
                });
            }}
            question="Remove this track from the library?"
            trigger={(open) => (
              <Button onClick={open} size="small" variant="ghost">
                <Trash2 aria-hidden="true" />
                Remove
              </Button>
            )}
          >
            Any playlist holding it loses it, on every feed that plays that
            playlist.
          </InlineConfirm>
          <div className="flex items-center gap-2">
            <Button onClick={onClose} variant="ghost">
              Close
            </Button>
            <Button disabled={busy || !cueValid} type="submit">
              {busy ? "Saving…" : "Save track"}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

/** The playable stretch, drawn. Trimmed audio at each end is shown, not hidden. */
function CueBar({
  cueInMs,
  cueOutMs,
  durationMs,
}: {
  cueInMs: number;
  cueOutMs: number | null;
  durationMs: number;
}) {
  const safeDuration = durationMs > 0 ? durationMs : 1;
  const start = Math.max(0, Math.min(1, (cueInMs || 0) / safeDuration));
  const end = Math.max(
    start,
    Math.min(1, (cueOutMs ?? safeDuration) / safeDuration),
  );
  return (
    <div
      aria-hidden="true"
      className="mt-4 h-8 w-full overflow-hidden rounded-sm border border-border bg-surface"
    >
      <div className="flex h-full w-full">
        <span
          className="h-full bg-[repeating-linear-gradient(135deg,var(--surface-hi)_0_6px,transparent_6px_12px)]"
          style={{ width: `${String(start * 100)}%` }}
        />
        <span
          className="h-full bg-accent-soft"
          style={{ width: `${String((end - start) * 100)}%` }}
        />
        <span
          className="h-full bg-[repeating-linear-gradient(135deg,var(--surface-hi)_0_6px,transparent_6px_12px)]"
          style={{ width: `${String((1 - end) * 100)}%` }}
        />
      </div>
    </div>
  );
}
